import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../config/apiClient';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type FieldKey = 'current' | 'new' | 'confirm';

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState<Record<FieldKey, boolean>>({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Modal her acildiginda / kapandiginda formu temizle
  const resetForm = useCallback(() => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShow({ current: false, new: false, confirm: false });
    setLoading(false);
    setFormError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleClose = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const toggleShow = (key: FieldKey) => () => {
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setFormError(null);

    // Frontend validasyon
    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormError(t('changePasswordModal.errors.allRequired'));
      return;
    }
    if (newPassword.length < 6) {
      setFormError(t('changePasswordModal.errors.tooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError(t('changePasswordModal.errors.passwordMismatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setFormError(t('changePasswordModal.errors.sameAsOld'));
      return;
    }

    setLoading(true);
    try {
      // Own-auth: korumalı uç (Authorization: Bearer header'ı authApi interceptor'ı tarafından eklenir).
      await authApi.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      console.log('[ChangePassword] Sifre basariyla guncellendi (own-auth)');
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.Code ?? err?.response?.data?.code;
      let message: string;
      if (status === 401) {
        message = t('changePasswordModal.errors.requiresRecentLogin');
      } else if (code === 'PASSWORD_PENDING') {
        message = t('changePasswordModal.errors.wrongPassword');
      } else if (status === 400) {
        // Sunucu "mevcut şifre hatalı" veya validasyon hatası döndürür.
        message = t('changePasswordModal.errors.wrongPassword');
      } else if (status === 429) {
        message = t('changePasswordModal.errors.tooManyRequests');
      } else if (!err?.response) {
        message = t('changePasswordModal.errors.network');
      } else {
        message = t('changePasswordModal.errors.generic');
      }
      console.error('[ChangePassword] Hata:', status, err?.response?.data);
      setFormError(message);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
        <LockResetIcon color="primary" />
        <Box sx={{ flexGrow: 1 }}>{t('changePasswordModal.title')}</Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={loading}
          aria-label="close"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('changePasswordModal.subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('changePasswordModal.currentPassword')}
              type={show.current ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
              fullWidth
              size="small"
              autoComplete="current-password"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShow('current')}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={show.current ? t('changePasswordModal.hidePassword') : t('changePasswordModal.showPassword')}
                    >
                      {show.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={t('changePasswordModal.newPassword')}
              type={show.new ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              disabled={loading}
              helperText={t('changePasswordModal.errors.tooShort')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShow('new')}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={show.new ? t('changePasswordModal.hidePassword') : t('changePasswordModal.showPassword')}
                    >
                      {show.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={t('changePasswordModal.confirmPassword')}
              type={show.confirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              disabled={loading}
              error={confirmPassword.length > 0 && confirmPassword !== newPassword}
              helperText={
                confirmPassword.length > 0 && confirmPassword !== newPassword
                  ? t('changePasswordModal.errors.passwordMismatch')
                  : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShow('confirm')}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={show.confirm ? t('changePasswordModal.hidePassword') : t('changePasswordModal.showPassword')}
                    >
                      {show.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {formError && (
              <Alert severity="error" variant="outlined" sx={{ mt: 0.5 }}>
                {formError}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={loading} color="inherit">
            {t('changePasswordModal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
          >
            {loading ? t('changePasswordModal.submitting') : t('changePasswordModal.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordModal;
